import { FC, ReactElement, useState } from 'react'
import type { ActivityResponse, Feed, FeedsClient } from '@stream-io/feeds-client'
import classnames from 'classnames'
import { formatDistanceToNow } from 'date-fns'

import { Avatar } from '@/components'
import { ImageAttachmentPreview } from '@/compositions'
import { Post, PostType, ReactionType } from '@/entities/Post/Post'
import { getDateFnsLocale } from '@/helpers/date'

import { CommentsSection, PostActions } from './subcomponents'

import './PostCard.scss'

interface PostCardProps {
    post: Post
    activity: ActivityResponse
    feed: Feed
    client: FeedsClient
    highlighted?: boolean
    onToggleReaction: (activityId: string, type: ReactionType, isActive: boolean) => void
}

export const PostCard: FC<PostCardProps> = ({
    post,
    activity,
    feed,
    client,
    highlighted = false,
    onToggleReaction,
}): ReactElement => {
    const [commentsExpanded, setCommentsExpanded] = useState(false)

    const cardClassName = classnames('post-card', {
        'post-card--announcement': post.type === PostType.announcement,
        'post-card--highlighted': highlighted,
    })

    return (
        <article id={`post-${post.id}`} className={cardClassName}>
            <header className="post-card__header">
                <Avatar
                    className="post-card__avatar"
                    image={post.author.image}
                    initials={post.author.name?.charAt(0)}
                    name={post.author.name}
                />
                <div className="post-card__meta">
                    <span className="post-card__author">{post.author.name ?? post.author.id}</span>
                    <time className="post-card__time" dateTime={post.createdAt.toISOString()}>
                        {formatDistanceToNow(post.createdAt, { addSuffix: true, locale: getDateFnsLocale() })}
                    </time>
                </div>
            </header>

            <div className="post-card__content">
                {post.title && (
                    <h2 className="post-card__title">{post.title}</h2>
                )}

                {post.text && (
                    <p className="post-card__text">{post.text}</p>
                )}

                {!! post.images.length && (
                    <ImageAttachmentPreview images={post.images} className="post-card__image-preview" />
                )}
            </div>

            <PostActions
                post={post}
                commentsExpanded={commentsExpanded}
                onToggleLike={() => onToggleReaction(post.id, 'like', post.hasLiked)}
                onToggleHeart={() => onToggleReaction(post.id, 'heart', post.hasHearted)}
                onToggleComments={() => setCommentsExpanded(expanded => ! expanded)}
            />

            {commentsExpanded && (
                <CommentsSection
                    feed={feed}
                    activity={activity}
                    client={client}
                />
            )}
        </article>
    )
}
